import { GtmSelectExtrasUseCase } from './../../../core/domain/gtm/usecases/gtmSelectExtras.usecase';
import {
    AfterViewInit,
    ChangeDetectorRef,
    Component,
    ElementRef,
    OnInit,
    ViewChild,
} from '@angular/core';
import { Store } from '@ngrx/store';
import { updateReservation } from '@/core/reservation/store/reservation.actions';
import { Router } from '@angular/router';
import { getExtrasUseCase } from '../../../core/domain/extras/usecases/getExtras.usecase';
import {
    Insurance,
    Product,
} from '@/core/domain/extras/models/extras-data.model';
import { BreakpointService } from '@/common/services/breakpoint/breakpoint.service';
import { selectReservation } from '@/core/reservation/store/reservation.selector';
import { catchError, first, of, switchMap, take, throwError } from 'rxjs';
import { sendCartStep2InsuranceUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Insurance.usecase';
import { CartStep2InsuranceInterface } from '@/core/domain/cart-step2/models/cart-step2-insurance.model';
import { CartStep2ProductInterface } from '@/core/domain/cart-step2/models/cart-step2-product.model';
import { sendCartStep2ProductUseCase } from '@/core/domain/cart-step2/usecases/sendCartStep2Product.usecase';
import { GtmAddToCartUseCase } from '@/core/domain/gtm/usecases/gtmAddToCart.usecase';
import { UrlService } from '@/common/services/url/url.service';
import { AlertService } from '@/common/services/alert/alert.service';
import { CreateCartInternationalUseCase } from '@/core/domain/cart/usecases/createCartInternational.usecase';
import { CartCoverages } from '@/core/domain/cart/models/cart-request.model';
import _ from 'lodash';
import { CurrentReservationService } from '@/common/services/reservation/current';
import { ProgressBarComponent } from '@/ui/shared/components/progress-bar/progress-bar.component';
import { GetSummaryUseCase } from '@/core/domain/summary/usecases/getSummary.usecase';
import { GetUpdateSummaryUseCase } from '@/core/domain/update-category/usecases/getSetSummary.usecase';
import { UpdateSummary } from '@/core/domain/update-category/models/update-summary.model';
import { SsrService } from '@/common/services/ssr/ssr.service';

@Component({
    selector: 'fe-step-3',
    templateUrl: './step-3.component.html',
    styleUrls: ['./step-3.component.scss'],
})
export class Step3Component implements OnInit, AfterViewInit {
    public responseInsurance?: Insurance[];
    public responseProduct?: Product[];
    public rentDays?: number;
    public freeDays?: number;
    public activeProducts: Product[] = [];
    public activeInsurances: Insurance[] = [];
    public reservation: any;
    public updateSummary?: UpdateSummary | undefined;

    public currency?: string;
    public isLoadingUpdateSummary = true;
    public loadingPrice = false;

    public loadingBtnContinue = true;

    @ViewChild('progressBarDesktop') progressBarDesktop!: ProgressBarComponent;
    @ViewChild('extrasSection') extrasSection?: ElementRef<HTMLElement>;

    private hasScrolledToExtras = false;

    private readonly extrasScrollOffset = 100;

    get isMissingInsurance(): boolean {
        const origin =
            this.currentReservation.current.origin ?? this.reservation?.origin;
        return origin === 'NAT' && this.activeInsurances.length === 0;
    }

    get footerButtonKey(): string {
        if (this.isMissingInsurance) {
            return 'fe_details_step3_main_wrapper_footer_btn_footer_no_coverage';
        }
        return this.currentReservation.current.flowType === 'office'
            ? 'fe_details_step3_main_wrapper_footer_btn_footer'
            : 'fe_details_step3_main_wrapper_footer_btn_footer_prepay';
    }

    constructor(
        private readonly getExtrasUseCase: getExtrasUseCase,
        private readonly gtmSelectExtrasUseCase: GtmSelectExtrasUseCase,
        private readonly gtmAddToCartUseCase: GtmAddToCartUseCase,
        private readonly store: Store,
        private readonly router: Router,
        private readonly sendCartStep2InsuranceUseCase: sendCartStep2InsuranceUseCase,
        private readonly sendCartStep2ProductUseCase: sendCartStep2ProductUseCase,
        private readonly getUpdateSummaryUseCase: GetUpdateSummaryUseCase,
        public breakpoint: BreakpointService,
        private readonly url: UrlService,
        private readonly alert: AlertService,
        private readonly createCartInternationalUseCase: CreateCartInternationalUseCase,
        public currentReservation: CurrentReservationService,
        private readonly cdr: ChangeDetectorRef,
        private readonly ssr: SsrService,
    ) {}

    ngAfterViewInit(): void {
        this.cdr.detectChanges();
    }

    ngOnInit(): void {
        this.store
            .select(selectReservation)
            .pipe(
                switchMap((reservation) => {
                    this.reservation = reservation;
                    this.currency = this.reservation.currency;
                    return of(reservation);
                }),
            )
            .subscribe();
        this.getExtras();
        this.resetProducts();
    }

    private async resetProducts(): Promise<void> {
        this.store.dispatch(
            updateReservation({
                value: {
                    products: [],
                },
            }),
        );
        this.store
            .select(selectReservation)
            .pipe(
                take(1),
                switchMap((reservation) => {
                    if (
                        this.currentReservation.current.origin === 'NAT' ||
                        reservation.origin === 'NAT'
                    ) {
                        const productData: CartStep2ProductInterface = {
                            sessionId: reservation.uuid,
                            products: [],
                            isPrePaid: true,
                        };
                        this.sendCartStep2ProductUseCase
                            .execute(productData)
                            .pipe(
                                switchMap((response) => {
                                    this.loadingBtnContinue = false;
                                    this.currentReservation.total =
                                        response.data?.amount;
                                    this.store.dispatch(
                                        updateReservation({
                                            value: {
                                                total: response.data?.amount,
                                            },
                                        }),
                                    );
                                    this.getUpdateSummary();
                                    return of(true);
                                }),
                                catchError(() => {
                                    this.loadingBtnContinue = false;
                                    this.isLoadingUpdateSummary = false;
                                    return of(null);
                                }),
                            )
                            .subscribe();
                    } else {
                        this.isLoadingUpdateSummary = false;
                        this.createCartInternationalUseCase
                            .execute([])
                            .pipe(
                                switchMap((response) => {
                                    this.loadingBtnContinue = false;
                                    this.currentReservation.total =
                                        response.data?.amount;
                                    this.store.dispatch(
                                        updateReservation({
                                            value: {
                                                total: response.data?.amount,
                                            },
                                        }),
                                    );
                                    return of(true);
                                }),
                                catchError((error) => {
                                    this.loadingBtnContinue = false;
                                    this.alert.openDefault(error);
                                    return throwError(() => error);
                                }),
                            )
                            .subscribe();
                    }

                    return of(reservation);
                }),
            )
            .subscribe();
    }

    public getExtras(): void {
        const id = this.reservation.locationSelected;
        const coupon = this.reservation.discountCode ?? undefined;
        const category = this.reservation.vehicle.id ?? undefined;
        const pickupDate =
            this.reservation.startDate ?? this.currentReservation.startDatetime;
        const returnDate =
            this.reservation.endDate ?? this.currentReservation.endDatetime;

        this.getExtrasUseCase
            .execute({ id, coupon, category, pickupDate, returnDate })
            .subscribe((extras) => {
                this.responseInsurance = extras.data?.extras.insurances;
                this.responseProduct = extras.data?.extras.products;
                this.rentDays = extras.data?.rentDays;
                this.freeDays = extras.data?.freeDays;
            });
    }

    updateActiveProducts(products: Product[]): void {
        this.activeProducts = products;
        this.loadingBtnContinue = true;
        this.store.dispatch(
            updateReservation({
                value: {
                    products: this.activeProducts,
                },
            }),
        );
        if (this.currentReservation.current.origin === 'NAT') {
            this.store
                .select(selectReservation)
                .pipe(
                    take(1),
                    switchMap((reservation) => {
                        const productIds = reservation.products.map(
                            (product: Product) => product.id,
                        );

                        const extras = reservation.products.map(
                            (product: any) => [
                                {
                                    name: product.name,
                                    value: product.price,
                                    quantity: 1,
                                },
                            ],
                        );

                        const productData: CartStep2ProductInterface = {
                            sessionId: reservation.uuid,
                            products: productIds,
                            isPrePaid: true,
                        };

                        const additionalDriverIndex =
                            reservation.products.findIndex(
                                (product: Product) => product.id === 0,
                            );

                        if (additionalDriverIndex !== -1) {
                            productData.extras = extras[additionalDriverIndex];
                        }

                        return this.sendCartStep2ProductUseCase
                            .execute(productData)
                            .pipe(
                                switchMap((response) => {
                                    this.store.dispatch(
                                        updateReservation({
                                            value: {
                                                total: response.data?.amount,
                                            },
                                        }),
                                    );
                                    this.loadingBtnContinue = false;
                                    return of(true);
                                }),
                                catchError((error) => {
                                    this.loadingBtnContinue = false;
                                    this.alert.openDefault(error);
                                    return of(null);
                                }),
                            );
                    }),
                )
                .subscribe();
        }
    }

    private updateInsurancesNat(): void {
        this.loadingBtnContinue = true;
        this.store
            .select(selectReservation)
            .pipe(
                take(1),
                switchMap((reservation) => {
                    const insuranceData: CartStep2InsuranceInterface = {
                        sessionId: reservation.uuid,
                        insuranceId: _.get(reservation, 'insurances[0].id'),
                        isPrePaid: false,
                    };

                    return this.sendCartStep2InsuranceUseCase
                        .execute(insuranceData)
                        .pipe(
                            switchMap((response) => {
                                this.store.dispatch(
                                    updateReservation({
                                        value: {
                                            total: response.data?.amount,
                                        },
                                    }),
                                );
                                this.loadingBtnContinue = false;
                                return of(true);
                            }),
                            catchError((error) => {
                                this.alert.openDefault(error);
                                return of(null);
                            }),
                        );
                }),
            )
            .subscribe((isSuccessful) => {
                if (isSuccessful) {
                    if (!this.isMissingInsurance) {
                        this.gtmAddToCartUseCase.execute().subscribe();
                    }
                    setTimeout(() => {
                        this.getUpdateSummary();
                    }, 1000);
                }
                if (!isSuccessful) {
                    console.error('Navigation cancelled due to an error.');
                }
            });
    }

    private updateInsurancesInt(): void {
        this.loadingBtnContinue = true;
        const coverages: CartCoverages[] = [];
        _.forEach(this.activeInsurances, (item) => {
            coverages.push({
                code: _.toString(item.code),
                type: _.toString(item.type),
            });
        });
        this.createCartInternationalUseCase
            .execute(coverages)
            .pipe(
                switchMap((response) => {
                    this.store.dispatch(
                        updateReservation({
                            value: {
                                total: response.data?.amount,
                            },
                        }),
                    );
                    this.loadingBtnContinue = false;
                    return of(true);
                }),
                catchError((error) => {
                    this.loadingBtnContinue = false;
                    this.alert.openDefault(error);
                    return throwError(() => error);
                }),
            )
            .subscribe();
    }

    private scrollToExtras(): void {
        if (!this.ssr.isPlatformBrowser() || this.hasScrolledToExtras) {
            return;
        }

        if (!this.responseProduct?.length) {
            return;
        }

        this.hasScrolledToExtras = true;

        requestAnimationFrame(() => this.alignToExtras());
    }

    private alignToExtras(): void {
        const target = this.extrasSection?.nativeElement;

        if (!target) {
            return;
        }

        const progressBar = document.querySelector('fe-progress-bar');
        const stickyHeight = this.breakpoint.isMobile()
            ? 0
            : (progressBar?.clientHeight ?? 0);

        const top =
            target.getBoundingClientRect().top +
            window.scrollY -
            stickyHeight -
            this.extrasScrollOffset;

        window.scrollTo({ top: Math.max(top, 0), behavior: 'smooth' });
    }

    updateActiveInsurances(insurances: Insurance[]): void {
        this.activeInsurances = insurances;

        if (
            this.currentReservation.current.origin === 'NAT' &&
            insurances.length > 0
        ) {
            this.scrollToExtras();
        }

        this.store.dispatch(
            updateReservation({
                value: {
                    insurances: this.activeInsurances,
                    insurancesSessionId: this.reservation?.uuid,
                },
            }),
        );
        this.store
            .select(selectReservation)
            .pipe(
                first(),
                switchMap((reservation) => {
                    if (reservation.origin === 'NAT') {
                        this.loadingPrice = true;
                        this.updateInsurancesNat();
                    } else {
                        this.updateInsurancesInt();
                    }
                    return of(reservation);
                }),
            )
            .subscribe();
    }

    handleFooterClick(): void {
        if (!this.responseProduct || !this.responseInsurance) return;

        this.store.dispatch(
            updateReservation({
                value: {
                    products: this.activeProducts,
                    insurances: this.activeInsurances,
                },
            }),
        );

        this.loadingBtnContinue = true;

        this.gtmSelectExtrasUseCase.execute().subscribe(() => {
            this.nextStep();
        });
    }

    private getUpdateSummary() {
        this.getUpdateSummaryUseCase.execute().subscribe({
            next: (responseSummary) => {
                if (this.updateSummary) {
                    this.updateSummary.total =
                        responseSummary?.data?.total ?? 0;
                } else {
                    this.isLoadingUpdateSummary = true;
                    this.updateSummary = responseSummary?.data;
                }
                this.isLoadingUpdateSummary = false;
                this.loadingPrice = false;
            },
            error: () => {
                this.loadingPrice = false;
                this.isLoadingUpdateSummary = false;
            },
        });
    }
    emptyNext(): void {
        this.nextStep();
        /* if (!this.responseProduct || !this.responseInsurance) {
            return;
        }

        this.store.dispatch(
            updateReservation({
                value: {
                    products: [],
                    insurances: this.activeInsurances,
                },
            }),
        );

        this.loadingBtnContinue = true;

        const productData: CartStep2ProductInterface = {
            sessionId: this.currentReservation.current.sessionId as string,
            products: [],
            isPrePaid: true,
        };
        this.sendCartStep2ProductUseCase
            .execute(productData)
            .pipe(
                switchMap((response) => {
                    this.store.dispatch(
                        updateReservation({
                            value: {
                                total: response.data?.amount,
                            },
                        }),
                    );
                    this.loadingBtnContinue = false;
                    return of(true);
                }),
                catchError(() => {
                    this.loadingBtnContinue = false;
                    return of(null);
                }),
            )
            .subscribe(() => this.nextStep());*/
    }

    nextStep(): void {
        this.router.navigate([
            this.url.format('reservations/reservation-summary'),
        ]);
    }
}
